import type { AppHydraItem } from "@verbleif/lib/src/composables/Api/Type/AppStore/app.interface";
import type { AppSubscriptionSyncImportHydraItem } from "@verbleif/lib/src/composables/Api/Type/AppStore/app_subscription.interface";
import type { LabelHydraItem } from "@verbleif/lib/src/composables/Api/Type/Label/label.interface";
import type { ProductHydraItem } from "@verbleif/lib/src/composables/Api/Type/Product/product.interface";
import type { PropertyHydraItem } from "@verbleif/lib/src/composables/Api/Type/Property/property.interface";
import type { PropertyGroupHydraItem } from "@verbleif/lib/src/composables/Api/Type/PropertyGroup/property_group.interface";
import type { PropertyRouteHydraCollectionItem } from "@verbleif/lib/src/composables/Api/Type/PropertyRoute/propertyRoute.interface";
import type { ReportTopicHydraItem } from "@verbleif/lib/src/composables/Api/Type/ReportTopic/report_topic.interface";
import type { SortHydraItem } from "@verbleif/lib/src/composables/Api/Type/Sort/sort.interface";
import type { UserHydraItem } from "@verbleif/lib/src/composables/Api/Type/User/user.interface";
import type { UserGroupHydraItem } from "@verbleif/lib/src/composables/Api/Type/UserGroup/user_group.interface";
import { SortFieldEnum, SortTypeEnum } from "@verbleif/lib/src/composables/Api/Type/Sort/sort.enum";
import { StorageSerializers, useStorage } from "@vueuse/core";
import { computed, ref } from "vue";

export interface TaskSidebar {
  shortcuts: boolean
  departments: boolean
  walkRoutes: boolean
  perObject: boolean
}

export interface ReportSidebar {
  general: boolean
  reportTopics: boolean
  reportSorts: boolean
  perObject: boolean
}

function defaultTaskSidebar(): TaskSidebar {
  return {
    shortcuts: true,
    departments: false,
    walkRoutes: true,
    perObject: false,
  };
}

function defaultReportSidebar(): ReportSidebar {
  return {
    general: true,
    reportTopics: true,
    reportSorts: true,
    perObject: false,
  };
}

export interface ReservationSidebar {
  shortcuts: boolean
}

export interface TaskSortPreference {
  field: string | null
  direction: "ASC" | "DESC"
}

function createSystemStore() {
  const systemUsers = ref<UserHydraItem[]>([]);
  const sorts = ref<SortHydraItem[]>([]);
  const propertyGroups = ref<PropertyGroupHydraItem[]>([]);
  const properties = ref<PropertyHydraItem[]>([]);
  const products = ref<ProductHydraItem[]>([]);
  const activeApps = ref<AppHydraItem[]>([]);
  const reportTopics = ref<ReportTopicHydraItem[]>([]);
  const propertyRoutes = ref<PropertyRouteHydraCollectionItem[]>([]);
  const appSubscriptionSyncImportBatches = ref<AppSubscriptionSyncImportHydraItem[]>([]);
  const labels = ref<LabelHydraItem[]>([]);
  const searchValue = ref<string>("");
  // mergeDefaults gives sections added later their default state when an older state is stored.
  const taskSidebar = useStorage<TaskSidebar>("taskSidebar", defaultTaskSidebar(), undefined, {
    serializer: StorageSerializers.object,
    mergeDefaults: true,
  });
  const reportSidebar = useStorage<ReportSidebar>("reportSidebar", defaultReportSidebar(), undefined, {
    serializer: StorageSerializers.object,
    mergeDefaults: true,
  });
  const reservationSidebar = useStorage<ReservationSidebar>("reservationSidebar", {
    shortcuts: true,
  }, undefined, {
    serializer: StorageSerializers.object,
  });
  const taskOverviewSettings = useStorage<{
    useSimpleFilters?: boolean
    taskType?: "root" | "all" | "sub"
    autoRefreshEnabled?: boolean
    autoRefreshInterval?: number
    groupBy?: string
  }>(
    "taskOverviewSettingsV2",
    {
      useSimpleFilters: true,
      taskType: undefined,
      autoRefreshEnabled: true,
      autoRefreshInterval: 60,
      groupBy: undefined,
    },
    undefined,
    { serializer: StorageSerializers.object },
  );

  // One-time migration: Add auto-refresh properties to existing users' settings
  if (taskOverviewSettings.value.autoRefreshEnabled === undefined) {
    taskOverviewSettings.value = {
      ...taskOverviewSettings.value,
      autoRefreshEnabled: true,
      autoRefreshInterval: 60,
    };
  }

  // Migration-safe computed for taskOverviewSettings
  const taskOverviewSettingsComputed = computed(() => {
    const raw = taskOverviewSettings.value;
    return {
      useSimpleFilters: raw.useSimpleFilters ?? true,
      taskType: raw.taskType,
      autoRefreshEnabled: raw.autoRefreshEnabled ?? true,
      autoRefreshInterval: raw.autoRefreshInterval ?? 60,
      groupBy: raw.groupBy,
    };
  });
  const reportOverviewSettings = useStorage<{
    useSimpleFilters?: boolean
    autoRefreshEnabled?: boolean
    autoRefreshInterval?: number
    groupBy?: string
  }>(
    "reportOverviewSettingsV2",
    {
      useSimpleFilters: true,
      autoRefreshEnabled: true,
      autoRefreshInterval: 60,
      groupBy: undefined,
    },
    undefined,
    { serializer: StorageSerializers.object },
  );

  // One-time migration: Add auto-refresh properties to existing users' settings
  if (reportOverviewSettings.value.autoRefreshEnabled === undefined) {
    reportOverviewSettings.value = {
      ...reportOverviewSettings.value,
      autoRefreshEnabled: true,
      autoRefreshInterval: 60,
    };
  }

  // Migration-safe computed for reportOverviewSettings
  const reportOverviewSettingsComputed = computed(() => {
    const raw = reportOverviewSettings.value;
    return {
      useSimpleFilters: raw.useSimpleFilters ?? true,
      autoRefreshEnabled: raw.autoRefreshEnabled ?? true,
      autoRefreshInterval: raw.autoRefreshInterval ?? 60,
      groupBy: raw.groupBy,
    };
  });

  const taskSortPreferences = useStorage<Partial<Record<string, TaskSortPreference>>>(
    "taskSortPreferences",
    {},
    undefined,
    { serializer: StorageSerializers.object },
  );

  function getTaskSortPreference(listType: string): TaskSortPreference | undefined {
    return taskSortPreferences.value[listType];
  }

  function setTaskSortPreference(listType: string, preference: TaskSortPreference): void {
    taskSortPreferences.value[listType] = preference;
  }

  const systemUsersIndexedByIri = computed<Record<string, UserHydraItem>>(() => {
    return systemUsers.value.reduce((acc, obj) => {
      const { "@id": key } = obj;
      acc[key] = obj;
      return acc;
    }, {} as Record<string, UserHydraItem>);
  });

  const activeAppsIndexedByIri = computed<Record<string, AppHydraItem>>(() => {
    return activeApps.value.reduce((acc, obj) => {
      const { "@id": key } = obj;
      acc[key] = obj;
      return acc;
    }, {} as Record<string, AppHydraItem>);
  });

  const sortsIndexedByIri = computed<Record<string, SortHydraItem>>(() => {
    return sorts.value.reduce((acc, obj) => {
      const { "@id": key } = obj;
      acc[key] = obj;
      return acc;
    }, {} as Record<string, SortHydraItem>);
  });

  const reportTopicsIndexedByIri = computed<Record<string, ReportTopicHydraItem>>(() => {
    return reportTopics.value.reduce((acc, obj) => {
      const { "@id": key } = obj;
      acc[key] = obj;
      return acc;
    }, {} as Record<string, ReportTopicHydraItem>);
  });

  const propertyRoutesIndexedByIri = computed<Record<string, PropertyRouteHydraCollectionItem>>(() => {
    return propertyRoutes.value.reduce((acc, obj) => {
      const { "@id": key } = obj;
      acc[key] = obj;
      return acc;
    }, {} as Record<string, PropertyRouteHydraCollectionItem>);
  });

  const labelsIndexedByIri = computed<Record<string, LabelHydraItem>>(() => {
    return labels.value.reduce((acc, label) => {
      acc[label["@id"]] = label;
      return acc;
    }, {} as Record<string, LabelHydraItem>);
  });

  const activePropertyRoutes = computed<PropertyRouteHydraCollectionItem[]>(() => {
    return propertyRoutes.value.filter(route => route.active);
  });

  const propertyGroupsIndexedByIri = computed<Record<string, PropertyGroupHydraItem>>(() => {
    return propertyGroups.value.reduce((acc: Record<string, PropertyGroupHydraItem>, obj) => {
      const { "@id": key } = obj;
      acc[key] = obj;
      return acc;
    }, {});
  });

  const propertiesIndexedByIri = computed<Record<string, PropertyHydraItem>>(() => {
    return properties.value.reduce((acc: Record<string, PropertyHydraItem>, obj) => {
      const { "@id": key } = obj;
      acc[key] = obj;
      return acc;
    }, {});
  });

  const productsIndexedByIri = computed<Record<string, ProductHydraItem>>(() => {
    return products.value.reduce((acc: Record<string, ProductHydraItem>, obj) => {
      const { "@id": key } = obj;
      acc[key] = obj;
      return acc;
    }, {});
  });

  const usersBasedOnRole = computed(() => {
    return [];
  });
  const assignableUsersTeamsDepartments = computed(() => {
    const defaultAssignableAssignees = [
      ...usersBasedOnRole.value,
    ];

    return defaultAssignableAssignees.sort((a: UserHydraItem | UserGroupHydraItem, b: UserHydraItem | UserGroupHydraItem) => {
      const nameA = a.name.toUpperCase();
      const nameB = b.name.toUpperCase();

      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
  });
  const filteredAssignableUserGroupsUsers = computed(() => {
    return assignableUsersTeamsDepartments.value.filter(() => {
      if (searchValue.value === "") {
        return true;
      }

      // if ("name" in item) {
      //   return item.name.toLowerCase().includes(searchValue.value.toLowerCase());
      // }

      return true;
    });
  });

  const isAnyAppSubscriptionSyncImportBatchRunning = computed(() => {
    return appSubscriptionSyncImportBatches.value.some(batch => !batch.finishedAt && !batch.failedAt);
  });

  function reset() {
    searchValue.value = "";
    taskOverviewSettings.value = {
      useSimpleFilters: true,
      taskType: undefined,
      autoRefreshEnabled: true,
      autoRefreshInterval: 60,
    };
    reportOverviewSettings.value = {
      useSimpleFilters: true,
      autoRefreshEnabled: true,
      autoRefreshInterval: 60,
    };
    taskSidebar.value = defaultTaskSidebar();
    reportSidebar.value = defaultReportSidebar();
    reservationSidebar.value = {
      shortcuts: true,
    };
    taskSortPreferences.value = {};
  }

  function setSearchValue(value: string) {
    searchValue.value = value;
  }

  function hasOneSort(type: "task" | "report") {
    if (type === "task") {
      return sorts.value.filter(sort => sort.type !== SortTypeEnum.REPORTS).length === 1;
    }

    return sorts.value.filter(sort => sort.type !== SortTypeEnum.TASKS).length === 1;
  }

  const hasInsideOnASort = computed<boolean>(() => {
    return sorts.value.some((sort) => {
      return sort.visibleFields?.includes(SortFieldEnum.INSIDE);
    });
  });

  const hasQuantityOnASort = computed<boolean>(() => {
    return sorts.value.some((sort) => {
      return sort.visibleFields?.includes(SortFieldEnum.QUANTITY);
    });
  });

  const hasPriorityOnASort = computed<boolean>(() => {
    return sorts.value.some((sort) => {
      return sort.visibleFields?.includes(SortFieldEnum.PRIORITY);
    });
  });

  function isImportRunningForAppSubscription(appSubscriptionIri: string) {
    return appSubscriptionSyncImportBatches.value.some(batch => batch.appSubscription === appSubscriptionIri && !batch.finishedAt && !batch.failedAt);
  }

  /** Imports are unique per install and location, so an import of another app at the same location does not count. */
  function isImportRunningForLocation(locationIri: string, appSubscriptionIri: string) {
    return appSubscriptionSyncImportBatches.value.some(batch => (
      batch.location === locationIri
      && batch.appSubscription === appSubscriptionIri
      && !batch.finishedAt
      && !batch.failedAt
    ));
  }

  function getAppSubscriptionSyncImportBatches(appSubscriptionIri: string) {
    return appSubscriptionSyncImportBatches.value.filter(batch => batch.appSubscription === appSubscriptionIri);
  }

  return {
    sorts,
    appSubscriptionSyncImportBatches,
    properties,
    getAppSubscriptionSyncImportBatches,
    isImportRunningForAppSubscription,
    isImportRunningForLocation,
    propertyGroups,
    products,
    searchValue,
    taskSidebar,
    reportSidebar,
    reservationSidebar,
    hasOneSort,
    taskOverviewSettings: taskOverviewSettingsComputed,
    taskOverviewSettingsRaw: taskOverviewSettings,
    reportTopics,
    isAnyAppSubscriptionSyncImportBatchRunning,
    systemUsersIndexedByIri,
    sortsIndexedByIri,
    propertyGroupsIndexedByIri,
    propertiesIndexedByIri,
    productsIndexedByIri,
    usersBasedOnRole,
    assignableUsersTeamsDepartments,
    filteredAssignableUserGroupsUsers,
    hasInsideOnASort,
    hasQuantityOnASort,
    hasPriorityOnASort,
    reportTopicsIndexedByIri,
    propertyRoutes,
    propertyRoutesIndexedByIri,
    activePropertyRoutes,
    labels,
    labelsIndexedByIri,
    reset,
    activeAppsIndexedByIri,
    activeApps,
    setSearchValue,
    reportOverviewSettings: reportOverviewSettingsComputed,
    reportOverviewSettingsRaw: reportOverviewSettings,
    taskSortPreferences,
    getTaskSortPreference,
    setTaskSortPreference,
  };
}

type SystemStoreInterface = ReturnType<typeof createSystemStore>;
let backwardsCompatibleInstance: null | SystemStoreInterface = null;
export function useSystemStore(): SystemStoreInterface {
  if (backwardsCompatibleInstance === null) {
    backwardsCompatibleInstance = createSystemStore();
  }

  return backwardsCompatibleInstance;
}
export default useSystemStore;
